import React from 'react';

// WakaTime Public Share URLs - Real live data from your account
const WAKATIME_SHARE_URLS = {
  // Your actual working WakaTime share URLs
  languageStats: 'https://wakatime.com/share/@Jokeseph/517f2f6d-2ee1-4012-9195-829bb623c2d7.svg', // Languages 7 days
  embeddableChart: 'https://wakatime.com/share/@Jokeseph/b6fb000c-2961-4299-b38e-de16fddb36c1.svg', // Coding Activity Table 7 days
  codingBadge: 'https://wakatime.com/badge/user/1a29077c-352e-4138-ae85-58f8b053a010.svg', // Code time badge
  profileLink: 'https://wakatime.com/@1a29077c-352e-4138-ae85-58f8b053a010'
};

// WakaTime Badge Component - Shows total coding time
export const WakaTimeBadge = ({ className = '', style = {} }) => {
  return (
    <a
      href={WAKATIME_SHARE_URLS.profileLink}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      style={style}
    >
      <img
        src={WAKATIME_SHARE_URLS.codingBadge}
        alt="Total time coded since Mar 1 2022"
        style={{
          height: 'auto',
          borderRadius: '4px'
        }}
      />
    </a>
  );
};

// WakaTime Embeddable Chart Component
export const WakaTimeEmbeddableChart = ({ className = '', style = {} }) => {
  return (
    <div className={className} style={style}>
      <figure style={{ margin: 0 }}>
        <embed
          src={WAKATIME_SHARE_URLS.embeddableChart}
          style={{
            width: '100%',
            height: 'auto',
            minHeight: '200px',
            borderRadius: '8px',
            background: 'transparent'
          }}
        />
      </figure>
    </div>
  );
};

// WakaTime Public Stats Component
export const WakaTimeLanguageStats = ({ className = '', style = {} }) => {
  return (
    <div className={className} style={style}>
      <img
        src={WAKATIME_SHARE_URLS.languageStats}
        alt="WakaTime Language Stats"
        style={{
          width: '100%',
          height: 'auto',
          borderRadius: '8px',
          background: 'transparent'
        }}
        onError={(e) => {
          // Fallback if image fails to load
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'block';
        }}
      />
      <div style={{ display: 'none', padding: '20px', textAlign: 'center', color: '#9ca3af' }}>
        <p>Language stats temporarily unavailable</p>
      </div>
    </div>
  );
};

export const wakatimeService = {
  // Get WakaTime URLs for embedding
  getShareUrls() {
    return WAKATIME_SHARE_URLS;
  },

  // Get badge URL for total coding time
  getBadgeUrl() {
    return WAKATIME_SHARE_URLS.codingBadge;
  },

  // Get profile link
  getProfileUrl() {
    return WAKATIME_SHARE_URLS.profileLink;
  },

  // Get language stats chart URL
  getLanguageStatsUrl() {
    return WAKATIME_SHARE_URLS.languageStats;
  },

  // Get embeddable chart URL
  getEmbeddableChartUrl() {
    return WAKATIME_SHARE_URLS.embeddableChart;
  },

  // Format time duration
  formatDuration(seconds) {
    if (!seconds) return '0 mins';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  },

  // Format time for display
  formatTime(totalSeconds) {
    if (!totalSeconds) return '--';
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h`;
    }
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    
    return `${minutes}m`;
  }
};

// Hook for using WakaTime data in React components - Now uses real WakaTime URLs
export const useWakaTimeStats = () => {
  const [stats, setStats] = React.useState({
    badgeUrl: wakatimeService.getBadgeUrl(),
    languageStatsUrl: wakatimeService.getLanguageStatsUrl(),
    embeddableChartUrl: wakatimeService.getEmbeddableChartUrl(),
    profileUrl: wakatimeService.getProfileUrl(),
    isLoading: false,
    error: null
  });

  // No need for API calls - we use direct WakaTime embed URLs
  React.useEffect(() => {
    // Just set the URLs immediately since they're static
    setStats({
      badgeUrl: wakatimeService.getBadgeUrl(),
      languageStatsUrl: wakatimeService.getLanguageStatsUrl(),
      embeddableChartUrl: wakatimeService.getEmbeddableChartUrl(),
      profileUrl: wakatimeService.getProfileUrl(),
      isLoading: false,
      error: null
    });
  }, []);

  return stats;
};

export default wakatimeService;
